import { useState } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { Phone } from 'lucide-react';
import { useBarStatus } from '@/hooks/useBarStatus';
import NavDropdown from '@/components/NavDropdown';
import MobileMenu from '@/components/MobileMenu';
import { TEL_DISPLAY, TEL_HREF } from '@/lib/contact';

const CARTA_LINKS = [
  { label: 'Las 12 Gildas',     to: '/gildas',   hint: 'Anchoa, piparra, oliva — y 9 más', img: '/dishes/thumbs/gilda-clasica.webp' },
  { label: 'Tortilla casera',   to: '/tortilla', hint: 'Cuajada al gusto, cada día',       img: '/dishes/thumbs/tortilla.webp' },
  { label: 'Bebidas y vermut',  to: '/bebidas',  hint: 'Vermut de grifo los domingos',     img: '/dishes/thumbs/bar-vermut.webp' },
  { label: 'Pedir bandeja',     to: '/pedido',   hint: 'Configura por WhatsApp',           img: '/dishes/thumbs/gilda-iberica.webp' },
];

const TOP_LINKS = [
  { label: 'Historia',  to: '/historia' },
  { label: 'Galería',   to: '/galeria' },
  { label: 'Visítanos', to: '/visitanos' },
  { label: 'Dossier',   to: '/dossier.pdf', download: 'Bar-Cabrero-Dossier.pdf' },
];

const MOBILE_ENTRIES = [
  { label: 'Carta', items: CARTA_LINKS },
  ...TOP_LINKS,
];

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const status = useBarStatus();
  const close = () => setMenuOpen(false);

  return (
    <>
      {/* TOP BAR */}
      <div className={`topbar ${status.open ? 'live' : 'closed'}`} data-testid="topbar">
        <span className="topbar-dot" aria-hidden="true" />
        <span data-testid="topbar-status">{status.msg}</span>
        <span className="topbar-sep" aria-hidden="true">·</span>
        <span className="topbar-addr">C. Julián Ceballos 19 · Torrelavega</span>
        <span className="topbar-sep" aria-hidden="true">·</span>
        <a href={TEL_HREF} className="topbar-tel" data-testid="topbar-tel">
          <Phone size={13} strokeWidth={2.2} aria-hidden="true" /> {TEL_DISPLAY}
        </a>
      </div>

      {/* MAIN NAV */}
      <nav data-testid="navbar" className={status.open ? 'is-live' : ''}>
        <Link to="/" className="nav-logo" onClick={close} data-testid="nav-logo">
          <span className="nav-logo-line1">Bar&nbsp;<em>Cabrero</em></span>
          <small>Est. 1928 · Torrelavega</small>
        </Link>

        <ul className="nav-links">
          <NavDropdown label="Carta" items={CARTA_LINKS} dataTestid="nav-carta-trigger" variant="carta" />
          {TOP_LINKS.map((l) => (
            <li key={l.to}>
              {l.download ? (
                <a
                  href={l.to}
                  download={l.download}
                  data-testid={`nav-${l.label.toLowerCase()}`}
                  onClick={close}
                >
                  {l.label}
                </a>
              ) : (
                <NavLink
                  to={l.to}
                  data-testid={`nav-${l.to.replace('/', '')}`}
                  className={({ isActive }) => (isActive ? 'active' : '')}
                  onClick={close}
                >
                  {l.label}
                </NavLink>
              )}
            </li>
          ))}
        </ul>

        <div className="nav-aside">
          {status.open && (
            <span className="nav-live-pill" data-testid="nav-live-pill" aria-label="El bar está abierto ahora">
              <span className="nav-live-dot" />
              Sirviendo ahora
            </span>
          )}
          <Link to="/pedido" className="nav-cta" data-testid="nav-cta-pedir" onClick={close}>
            Pedir bandeja
          </Link>
        </div>

        {/* hidden badge for backward-compat tests */}
        <span
          id="badge"
          data-testid="open-badge"
          className={status.open ? 'badge-open' : 'badge-closed'}
          style={{ position: 'absolute', left: '-9999px' }}
        >
          {status.open ? 'Abierto ahora' : 'Cerrado'}
        </span>

        <button
          className={`hamburger ${menuOpen ? 'open' : ''}`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Menú"
          aria-expanded={menuOpen}
          data-testid="hamburger-btn"
        >
          <span /><span /><span />
        </button>

        <MobileMenu open={menuOpen} entries={MOBILE_ENTRIES} onClose={close} />
      </nav>
    </>
  );
}
